/**
 * logo.dev image URLs for vendor logos.
 *
 * The publishable token is intentionally public: it is embedded in every logo
 * image the app renders, including generated buyer briefs, and logo.dev scopes
 * it to image fetches only.
 */
export const LOGO_DEV_TOKEN = "pk_DmFwLlBkSAyEWWyE_dUI2Q";
export const LOGO_DEV_BASE_URL = "https://img.logo.dev";

/**
 * Reduce a website as a user might type it ("https://www.Amazon.com/about",
 * "amazon.com", "www.amazon.com:443") to the bare domain logo.dev keys on.
 * Returns undefined when nothing that looks like a domain is present.
 */
export function extractDomain(website?: string): string | undefined {
    if (!website) return undefined;
    let host = website.trim().toLowerCase();
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, ""); // scheme
    host = host.split(/[/?#]/)[0]; // path, query, fragment
    host = host.replace(/:\d+$/, ""); // port
    host = host.replace(/^www\./, "");
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host)) return undefined;
    return host;
}

export interface LogoDevOptions {
    /** The default JPEG flattens a transparent logo onto white; PNG keeps the transparency. */
    format?: "jpg" | "png";
    /** "dark": the variant drawn for a dark background (a light mark), for a logo that is dark on transparent. */
    theme?: "light" | "dark";
}

export function logoDevUrl(domain: string, options: LogoDevOptions = {}): string {
    const params = new URLSearchParams({ token: LOGO_DEV_TOKEN });
    if (options.format) params.set("format", options.format);
    if (options.theme) params.set("theme", options.theme);
    return `${LOGO_DEV_BASE_URL}/${encodeURIComponent(domain)}?${params}`;
}

/** Convenience: website text straight to a logo URL, or undefined when no domain. */
export function logoDevUrlForWebsite(website?: string, options?: LogoDevOptions): string | undefined {
    const domain = extractDomain(website);
    return domain ? logoDevUrl(domain, options) : undefined;
}
